/**
 * viewScale — the page scale the inline script in client/index.html chose for
 * a NARROW screen (2026-10-28, iPhone 13 TestFlight screenshots).
 *
 * THE PROBLEM
 *   iOS Display Zoom (Settings > Display & Brightness > Display Zoom, where
 *   the zoomed option is literally called "Larger Text") makes an iPhone 13
 *   report a 320 x 693 pt screen instead of 390 x 844. Android's "Display
 *   size" does the same in dp. The WebView then lays the app out as if it
 *   were a 320px-wide phone: the hero wraps, the four stat cards squash, the
 *   tab labels crowd. Nothing is wrong with the CSS; the phone is lying about
 *   its size, and the app cannot tell the OS to stop.
 *
 * THE FIX
 *   What the app CAN do is lay itself out at its 390px design width and let
 *   the browser scale the whole page to fit the narrow screen (viewport
 *   `width=390, initial-scale=<short side / 390>`). Every CSS px then shrinks
 *   together (text, icons, spacing), so the page looks exactly like it does
 *   on a phone without Display Zoom. The script lives in index.html so the
 *   FIRST layout is already right; this module only exposes the number it
 *   picked.
 *
 * WHO NEEDS THE NUMBER
 *   Anything that crosses between CSS px and device points:
 *   - `env(safe-area-inset-*)`: WebKit hands the UIKit insets to CSS as raw
 *     points (WebPageCocoa.mm passes unobscuredSafeAreaInsets through,
 *     StyleEnvironmentVariables.cpp wraps them as px, no page-scale
 *     division), so index.css divides them by --nx-view-scale.
 *   - `screen.width/height` (backdropViewport.ts) are points too.
 *   - Rects sent to native code that expects points (the armed broadcast
 *     picker in broadcastAutoStart.ts).
 *   `window.innerWidth`, `100vw`, getBoundingClientRect() and visualViewport
 *   are all already in layout CSS px and need nothing.
 */

/** Width the app is designed for (iPhone 13/14/15 class, in CSS px). */
export const DESIGN_WIDTH_PX = 390;

/** Screens whose short side is narrower than this get the scaled layout. */
export const NARROW_SCREEN_PX = 360;

declare global {
  interface Window {
    /** Written by the inline viewport script in client/index.html. */
    __NEXUS_VIEW_SCALE__?: number;
  }
}

/**
 * Pure: the page scale for a screen whose short side is `shortSidePt` points.
 * 1 on every ordinary phone; 320/390 on a zoomed iPhone 13. Mirrors the inline
 * script, which cannot import this module.
 */
export function computeViewScale(shortSidePt: number): number {
  if (!Number.isFinite(shortSidePt) || shortSidePt <= 0) return 1;
  if (shortSidePt >= NARROW_SCREEN_PX) return 1;
  return Math.round((shortSidePt / DESIGN_WIDTH_PX) * 10000) / 10000;
}

/** The scale the running page was given: points per CSS px. 1 when unscaled. */
export function getViewScale(): number {
  if (typeof window === "undefined") return 1;
  const v = window.__NEXUS_VIEW_SCALE__;
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 1;
}

/** Convert a layout-CSS-px length to device points. */
export function cssPxToPoints(px: number, scale: number = getViewScale()): number {
  return px * scale;
}

/** Convert a device-point length to layout CSS px. */
export function pointsToCssPx(pt: number, scale: number = getViewScale()): number {
  return scale > 0 ? pt / scale : pt;
}
